// src/components/auth/ResetPassword.js
import React, { useState, useEffect } from "react";
import {
	Box,
	Card,
	CardContent,
	Container,
	TextField,
	Button,
	Typography,
	Alert,
	CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../config/supabase";

const ResetPassword = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [initializing, setInitializing] = useState(true);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);
	const [formData, setFormData] = useState({
		password: "",
		confirmPassword: "",
	});

	useEffect(() => {
		let mounted = true;
		let subscription = null;

		const initializePasswordReset = async () => {
			try {
				// Check if we have a hash or query params in the URL (Supabase recovery links)
				const hash = window.location.hash;
				const searchParams = new URLSearchParams(window.location.search);
				const hasRecoveryHash = hash && (
					hash.includes("type=recovery") || 
					hash.includes("access_token") ||
					hash.includes("recovery_token")
				);
				const hasRecoveryQuery = searchParams.get("type") === "recovery" || 
					searchParams.has("access_token") ||
					searchParams.has("recovery_token");
				const hasRecovery = hasRecoveryHash || hasRecoveryQuery;

				// Set up auth state listener first
				const {
					data: { subscription: authSubscription },
				} = supabase.auth.onAuthStateChange((event, session) => {
					if (!mounted) return;

					if (event === "PASSWORD_RECOVERY") {
						setError(""); // Clear any existing errors
						setInitializing(false);
					} else if (event === "SIGNED_OUT" && !session && !success) {
						// Only show error if we're not in the middle of a successful reset
						setError(
							"Your password reset session has expired. Please request a new password reset link."
						);
					}
				});

				subscription = authSubscription;

				// If there's a hash or query params, Supabase will process it automatically
				// We need to wait for Supabase to process the recovery token
				if (hasRecovery) {
					// Wait a bit for Supabase to process the hash
					await new Promise(resolve => setTimeout(resolve, 500));
					
					// Check if we now have a session (Supabase creates a recovery session)
					const { data: { session }, error: sessionError } = await supabase.auth.getSession();
					
					if (sessionError) {
						console.error("Session error:", sessionError);
					}

					if (mounted) {
						if (session) {
							setError("");
							setInitializing(false);
						} else {
							// Wait a bit more and check again (Supabase might still be processing)
							await new Promise(resolve => setTimeout(resolve, 1000));
							const { data: { session: retrySession } } = await supabase.auth.getSession();
							
							if (mounted) {
								if (retrySession) {
									setError("");
									setInitializing(false);
								} else {
									setError("Unable to process password reset link. Please request a new one.");
									setInitializing(false);
								}
							}
						}
					}
				} else {
					// No hash found - might be a direct navigation or expired link
					if (mounted) {
						// Check if there's already a session (user might have clicked link while logged in)
						const { data: { session } } = await supabase.auth.getSession();
						
						if (session) {
							// User has a session, allow them to proceed
							setError("");
							setInitializing(false);
						} else {
							setError("Invalid or expired password reset link. Please request a new one.");
							setInitializing(false);
						}
					}
				}
			} catch (err) {
				console.error("Error initializing password reset:", err);
				if (mounted) {
					setError("An error occurred. Please try again or request a new password reset link.");
					setInitializing(false);
				}
			}
		};

		initializePasswordReset();

		return () => {
			mounted = false;
			if (subscription) {
				subscription.unsubscribe();
			}
		};
	}, [success]);

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
		// Clear error when user starts typing
		if (error) {
			setError("");
		}
	};

	const validateForm = () => {
		if (!formData.password || !formData.confirmPassword) {
			setError("Please fill in all required fields");
			return false;
		}

		if (formData.password.length < 6) {
			setError("Password must be at least 6 characters long");
			return false;
		}

		if (formData.password !== formData.confirmPassword) {
			setError("Passwords do not match");
			return false;
		}

		return true;
	};

	const getErrorMessage = (error) => {
		const errorMessage = error.message || "";

		if (
			errorMessage.includes("Auth session missing") ||
			errorMessage.includes("session missing")
		) {
			return "Your password reset session has expired. Please request a new password reset link.";
		}

		if (
			errorMessage.includes(
				"New password should be different from the old password"
			)
		) {
			return "Please choose a password that is different from your current password.";
		}

		if (errorMessage.includes("Password should be at least")) {
			return "Password must be at least 6 characters long.";
		}

		if (
			errorMessage.includes("Invalid refresh token") ||
			errorMessage.includes("refresh_token_not_found")
		) {
			return "Your password reset link has expired. Please request a new password reset link.";
		}

		if (
			errorMessage.includes("User not found") ||
			errorMessage.includes("invalid_grant")
		) {
			return "Invalid or expired password reset link. Please request a new password reset.";
		}

		// Default fallback
		return (
			errorMessage ||
			"An error occurred while resetting your password. Please try again."
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!validateForm()) return;

		try {
			setLoading(true);
			setError("");

			// First check if we have a valid session
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (!session) {
				throw new Error("Auth session missing");
			}

			const { error } = await supabase.auth.updateUser({
				password: formData.password,
			});

			if (error) throw error;

			setSuccess(true);
			// Redirect to login after 3 seconds
			setTimeout(() => {
				navigate("/");
			}, 3000);
		} catch (error) {
			console.error("Password reset error:", error);
			setError(getErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container component="main" maxWidth="sm">
			<Box
				sx={{
					marginTop: 8,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}>
				<Box
					sx={{
						height: "48px",
						display: "flex",
						alignItems: "center",
						px: 2,
					}}>
					<img
						src="/logo.png"
						alt="Funmiyo Portal"
						style={{
							height: "45px",
							width: "auto",
							objectFit: "contain",
						}}
					/>
				</Box>
				<Card sx={{ width: "100%" }}>
					<CardContent>
						<Typography component="h1" variant="h5" align="center" gutterBottom>
							Reset Your Password
						</Typography>

						<Typography
							variant="body2"
							color="text.secondary"
							align="center"
							sx={{ mb: 2 }}>
							Please enter a new password that is different from your current
							password.
						</Typography>

						{error && (
							<Alert
								severity="error"
								sx={{ mt: 2 }}
								action={
									error.includes("expired") ||
									error.includes("Invalid") ||
									error.includes("session") ? (
										<Button
											color="inherit"
											size="small"
											onClick={() => navigate("/forgot-password")}>
											Get New Link
										</Button>
									) : null
								}>
								{error}
							</Alert>
						)}

						{success && (
							<Alert severity="success" sx={{ mt: 2 }}>
								Your password has been successfully reset. Redirecting to login
								page...
							</Alert>
						)}

						{initializing && (
							<Box sx={{ mt: 3, textAlign: "center" }}>
								<CircularProgress />
								<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
									Processing password reset link...
								</Typography>
							</Box>
						)}

						{!success && !initializing && (
							<Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
								<TextField
									margin="normal"
									required
									fullWidth
									name="password"
									label="New Password"
									type="password"
									id="password"
									autoComplete="new-password"
									value={formData.password}
									onChange={handleChange}
									helperText="Must be at least 6 characters and different from your current password"
								/>

								<TextField
									margin="normal"
									required
									fullWidth
									name="confirmPassword"
									label="Confirm New Password"
									type="password"
									id="confirmPassword"
									value={formData.confirmPassword}
									onChange={handleChange}
								/>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									sx={{ mt: 3, mb: 2 }}
									disabled={loading}>
									{loading ? <CircularProgress size={24} /> : "Reset Password"}
								</Button>

								<Button
									fullWidth
									variant="outlined"
									onClick={() => navigate("/")}
									sx={{ mt: 1 }}
									disabled={loading}>
									Back to Login
								</Button>
							</Box>
						)}
					</CardContent>
				</Card>
			</Box>
		</Container>
	);
};

export default ResetPassword;
